import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

import CreateAppointmentOverlay from "../../../../../_shared/_modules/create-appointment-overlay"
import { buildAppointmentCreateInit } from "../../../../../_shared/_logic/init"
import CreateBlockerOverlay from "../_modules/create-blocker-overlay"
import type {
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
} from "../../../../../../../../../api/kehrblatt/schedule-events"
import { addMinutes, setTime } from "../../../../../../../../../_shared/_logic/date-math"
import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"
import { parseDateLike } from "../../../../../../../_shared/_logic/date-bounds"
import type {
  EmployeeOption,
  EventOverlayOptions,
} from "../../../../../../../_shared/_logic/calendar-types"

export type CalendarCreateCommandContext = {
  organizationId: string | null
  loading: boolean
  eventsLoading: boolean
  now: Date
  slotMinutes: number
  dayEndHour: number
  selectedEmployee: string | string[]
  allowedEmployeeIds?: string[] | null
  canManageScheduling: boolean
  employees: EmployeeOption[]
  workOrders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  scheduleEvents: ScheduleEvent[]
  workingHoursByUser: WorkingHoursByUser
  targetEmployeeIds: string[]
  flexSlotMinutes: number | null
  flexSlotsPerDay: number | null
  upsertEvent: (event: ScheduleEvent) => void
  refreshEvents: () => Promise<void>
}

const asOverlayWorkOrders = (workOrders: CalendarBoardWorkOrderRef[]): WorkOrder[] =>
  workOrders as unknown as WorkOrder[]

const asOverlayProperties = (properties: CalendarBoardPropertyRef[]): Property[] =>
  properties as unknown as Property[]

const selectedEmployeeId = (selectedEmployee: string | string[]) =>
  Array.isArray(selectedEmployee) ? (selectedEmployee[0] ?? null) : selectedEmployee || null

const canWriteForEmployee = ({
  canManageScheduling,
  allowedEmployeeIds,
  employeeId,
}: {
  canManageScheduling: boolean
  allowedEmployeeIds?: string[] | null
  employeeId?: string | null
}) => {
  const scopeAllows =
    allowedEmployeeIds == null
      ? true
      : Boolean(employeeId && allowedEmployeeIds.includes(employeeId))

  return Boolean(canManageScheduling && scopeAllows)
}

const canLinkWorkOrderForEmployee = (
  context: CalendarCreateCommandContext,
  _employeeId?: string | null,
) => context.canManageScheduling

const resolveAppointmentEmployeeId = (
  context: CalendarCreateCommandContext,
  explicitEmployeeId?: string | null,
) => {
  if (explicitEmployeeId) return explicitEmployeeId

  const selectedEmployeeIds = Array.isArray(context.selectedEmployee)
    ? context.selectedEmployee
    : [context.selectedEmployee]

  return (
    selectedEmployeeIds.find((employeeId) => canWriteForEmployee({ ...context, employeeId })) ??
    context.targetEmployeeIds.find((employeeId) =>
      canWriteForEmployee({ ...context, employeeId }),
    ) ??
    context.employees.find((employee) =>
      canWriteForEmployee({ ...context, employeeId: employee.id }),
    )?.id ??
    null
  )
}

export const openCreateAppointmentFlow = (context: CalendarCreateCommandContext) => {
  if (context.loading || context.eventsLoading) {
    newNotification({ class: "info", html: "Kalender wird noch geladen. Bitte kurz warten." })

    return
  }

  void openCreateAppointmentDialog(context)
}

export const openCreateAppointmentDialog = async (
  context: CalendarCreateCommandContext,
  opts?: EventOverlayOptions,
) => {
  const organizationId = context.organizationId

  if (!organizationId) return

  const resolvedDay = parseDateLike(opts?.day) ?? parseDateLike(opts?.dayTs) ?? null
  const explicitStart = parseDateLike(opts?.startAt) ?? parseDateLike(opts?.startTs)
  const explicitTimeframeStart = parseDateLike(opts?.timeframeStart)

  const resolveDefaultStart = () => {
    if (explicitStart) return explicitStart

    if (explicitTimeframeStart) return explicitTimeframeStart

    if (opts?.startMinutes != null && resolvedDay) {
      return setTime(resolvedDay, Math.floor(opts.startMinutes / 60), opts.startMinutes % 60)
    }

    if (resolvedDay) {
      const roundedDefaultMinutes = Math.ceil((9 * 60) / context.slotMinutes) * context.slotMinutes

      return setTime(
        resolvedDay,
        Math.floor(roundedDefaultMinutes / 60),
        roundedDefaultMinutes % 60,
      )
    }

    return undefined
  }

  const defaultStart = resolveDefaultStart()
  const explicitEnd = parseDateLike(opts?.endAt) ?? parseDateLike(opts?.endTs)
  const explicitTimeframeEnd = parseDateLike(opts?.timeframeEnd)
  const defaultEnd = explicitEnd
    ? explicitEnd
    : explicitTimeframeEnd
      ? explicitTimeframeEnd
      : defaultStart
        ? addMinutes(
            defaultStart,
            opts?.endMinutes ? opts.endMinutes - (opts.startMinutes ?? 0) : context.slotMinutes,
          )
        : undefined

  const targetEmployee = resolveAppointmentEmployeeId(context, opts?.employeeId)

  if (!canWriteForEmployee({ ...context, employeeId: targetEmployee })) {
    newNotification({ class: "error", html: "Keine Berechtigung zum Anlegen oder Bearbeiten." })

    return
  }

  const canLinkWorkOrder = canLinkWorkOrderForEmployee(context, targetEmployee)
  const candidateEmployeeIds = context.employees
    .map((employee) => employee.id)
    .filter((employeeId) => canWriteForEmployee({ ...context, employeeId }))

  openOverlay({
    title: "Termin anlegen",
    content: CreateAppointmentOverlay,
    bodyPadding: "none",
    closeOnParentOpen: true,
    isDark: true,
    properties: {
      init: buildAppointmentCreateInit({
        organizationId,
        employees: context.employees,
        workOrders: asOverlayWorkOrders(context.workOrders),
        properties: asOverlayProperties(context.properties),
        customers: context.customers,
        contactPoints: context.contactPoints,
        scheduleEvents: context.scheduleEvents,
        workingHoursByUser: context.workingHoursByUser,
        candidateEmployeeIds,
        defaultEmployeeId: targetEmployee,
        defaultStart,
        defaultEnd,
        defaultFlexType: (opts?.flexType as ScheduleEventFlexType | undefined) ?? "SOFT",
        defaultStatus: "RESERVED" as ScheduleEventStatus,
        defaultWorkOrderId: null,
        defaultPropertyId: null,
        flexSlotMinutes: context.flexSlotMinutes ?? 60,
        flexSlotsPerDay: context.flexSlotsPerDay ?? 0,
        allowFreeAppointment: true,
        allowWorkOrderSelection: canLinkWorkOrder,
        allowNewWorkOrder: canLinkWorkOrder,
        defaultWorkOrderSource: "existing",
      }),
      onCreated: async (saved: ScheduleEvent) => {
        context.upsertEvent(saved)
        await context.refreshEvents()
      },
      onSwitchToBlocker: () =>
        openCreateBlockerFlow(context, {
          ...(opts ?? {}),
          employeeId: targetEmployee ?? undefined,
          startAt: defaultStart,
          endAt: defaultEnd,
          startTs: defaultStart?.getTime(),
          endTs: defaultEnd?.getTime(),
        }),
    },
  })
}

export const openCreateBlockerFlow = (
  context: CalendarCreateCommandContext,
  opts?: EventOverlayOptions,
) => {
  const organizationId = context.organizationId

  if (!organizationId) return

  if (context.loading || context.eventsLoading) {
    newNotification({ class: "info", html: "Kalender wird noch geladen. Bitte kurz warten." })

    return
  }

  const resolvedDay = parseDateLike(opts?.day) ?? parseDateLike(opts?.dayTs) ?? null
  const explicitTimeframeStart = parseDateLike(opts?.timeframeStart)
  const explicitStart =
    parseDateLike(opts?.startAt) ?? parseDateLike(opts?.startTs) ?? explicitTimeframeStart

  const targetEmployee = opts?.employeeId ?? selectedEmployeeId(context.selectedEmployee)

  if (!canWriteForEmployee({ ...context, employeeId: targetEmployee })) {
    newNotification({ class: "error", html: "Keine Berechtigung zum Anlegen." })

    return
  }

  const defaultStart =
    explicitStart ??
    (opts?.startMinutes != null && resolvedDay
      ? setTime(resolvedDay, Math.floor(opts.startMinutes / 60), opts.startMinutes % 60)
      : undefined)
  const explicitTimeframeEnd = parseDateLike(opts?.timeframeEnd)
  const defaultEnd =
    parseDateLike(opts?.endAt) ??
    parseDateLike(opts?.endTs) ??
    explicitTimeframeEnd ??
    (defaultStart
      ? addMinutes(defaultStart, opts?.endMinutes ? opts.endMinutes - (opts.startMinutes ?? 0) : 60)
      : undefined)

  openOverlay({
    title: "Blocker anlegen",
    content: CreateBlockerOverlay,
    bodyPadding: "none",
    closeOnParentOpen: true,
    isDark: true,
    properties: {
      init: {
        organizationId,
        employees: context.employees,
        scheduleEvents: context.scheduleEvents,
        workingHoursByUser: context.workingHoursByUser,
        defaultEmployeeId: targetEmployee,
        defaultStart,
        defaultEnd,
      },
      onCreated: async (savedEvents: ScheduleEvent[]) => {
        for (const saved of savedEvents) {
          context.upsertEvent(saved)
        }
        await context.refreshEvents()
      },
    },
  })
}
